import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { buildThemeClasses } from "../../nuvex-access/theme";
import { useBasesStore } from "../store";

// ---------------------------------------------------------------------------
// Reservas — month calendar of amenity bookings (the "when/who"). Reads the
// shared store so it reflects whatever Amenidades configured.
// ---------------------------------------------------------------------------

const pad = (n: number) => String(n).padStart(2, "0");

export default function ReservasCalendar() {
  const { t } = useTranslation("common");
  const tc = buildThemeClasses();
  const bookings = useBasesStore((s) => s.bookings);
  const amenities = useBasesStore((s) => s.amenities);

  const today = new Date();
  const [cursor, setCursor] = useState({
    year: today.getFullYear(),
    month: today.getMonth(), // 0-based
  });
  // "" = vista general (todas las amenidades); otherwise vista por amenidad.
  const [amenityFilter, setAmenityFilter] = useState("");

  const visible = useMemo(
    () =>
      amenityFilter
        ? bookings.filter((b) => b.amenityId === amenityFilter)
        : bookings,
    [bookings, amenityFilter],
  );

  const byDate = useMemo(() => {
    const map: Record<string, typeof bookings> = {};
    for (const b of visible) {
      (map[b.date] ??= []).push(b);
    }
    return map;
  }, [visible]);

  const monthNames = t("basesPropiedades.months", {
    returnObjects: true,
  }) as string[];
  const weekdays = t("basesPropiedades.weekdaysShort", {
    returnObjects: true,
  }) as string[];

  const firstDay = new Date(cursor.year, cursor.month, 1);
  const startOffset = (firstDay.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(cursor.year, cursor.month + 1, 0).getDate();

  const cells: (number | null)[] = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const move = (delta: number) => {
    setCursor((prev) => {
      const m = prev.month + delta;
      const year = prev.year + Math.floor(m / 12);
      const month = ((m % 12) + 12) % 12;
      return { year, month };
    });
  };

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
              <i className="ri-calendar-2-line text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>
                {t("basesPropiedades.reservas.calendarTitle")}
              </h1>
              <p className={`text-sm ${tc.textSecondary}`}>
                {monthNames[cursor.month]} {cursor.year}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={amenityFilter}
              onChange={(e) => setAmenityFilter(e.target.value)}
              className={`rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.cardBg} ${tc.text}`}
            >
              <option value="">
                {t("basesPropiedades.reservas.allAmenities")}
              </option>
              {amenities.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            <button
              onClick={() => move(-1)}
              className={`rounded-lg border px-3 py-2 ${tc.border} ${tc.text}`}
            >
              <i className="ri-arrow-left-s-line" />
            </button>
            <button
              onClick={() => setCursor({ year: today.getFullYear(), month: today.getMonth() })}
              className={`rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.text}`}
            >
              {t("basesPropiedades.reservas.today")}
            </button>
            <button
              onClick={() => move(1)}
              className={`rounded-lg border px-3 py-2 ${tc.border} ${tc.text}`}
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        </div>

        <div className={`rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
          <div className="grid grid-cols-7 gap-2">
            {weekdays.map((w) => (
              <div
                key={w}
                className={`pb-2 text-center text-xs font-medium ${tc.textSecondary}`}
              >
                {w}
              </div>
            ))}
            {cells.map((day, i) => {
              if (day === null) return <div key={`e${i}`} />;
              const key = `${cursor.year}-${pad(cursor.month + 1)}-${pad(day)}`;
              const dayBookings = byDate[key] ?? [];
              const isToday =
                day === today.getDate() &&
                cursor.month === today.getMonth() &&
                cursor.year === today.getFullYear();
              return (
                <div
                  key={key}
                  className={`min-h-[88px] rounded-lg border p-1.5 ${tc.border} ${
                    isToday ? "ring-2 ring-primary-500" : ""
                  }`}
                >
                  <div className={`mb-1 text-right text-xs ${tc.textSecondary}`}>
                    {day}
                  </div>
                  <div className="space-y-1">
                    {dayBookings.slice(0, 3).map((b) => (
                      <div
                        key={b.id}
                        title={`${b.amenityName} · ${b.customerName} · ${b.startTime}-${b.endTime}`}
                        className={`truncate rounded px-1 py-0.5 text-[10px] ${
                          b.status === "Confirmada"
                            ? "bg-emerald-500/15 text-emerald-600"
                            : b.status === "Cancelada"
                              ? "bg-red-500/15 text-red-600"
                              : "bg-amber-500/15 text-amber-600"
                        }`}
                      >
                        {b.startTime} {amenityFilter ? b.customerName : b.amenityName}
                      </div>
                    ))}
                    {dayBookings.length > 3 && (
                      <div className={`text-[10px] ${tc.textSecondary}`}>
                        +{dayBookings.length - 3}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-4">
          <span className="flex items-center gap-1.5 text-xs">
            <span className="h-3 w-3 rounded bg-emerald-500/40" />
            <span className={tc.textSecondary}>
              {t("basesPropiedades.status.confirmed")}
            </span>
          </span>
          <span className="flex items-center gap-1.5 text-xs">
            <span className="h-3 w-3 rounded bg-amber-500/40" />
            <span className={tc.textSecondary}>
              {t("basesPropiedades.status.pending")}
            </span>
          </span>
          <span className="flex items-center gap-1.5 text-xs">
            <span className="h-3 w-3 rounded bg-red-500/40" />
            <span className={tc.textSecondary}>
              {t("basesPropiedades.status.cancelled")}
            </span>
          </span>
        </div>

        <p className={`mt-4 text-xs ${tc.textSecondary}`}>
          <i className="ri-information-line mr-1" />
          {t("basesPropiedades.mockNotice")}
        </p>
      </div>
    </div>
  );
}
